/**
 * The sourcing API passes the raw Pearch profile shape through as-is.
 *
 * Pearch wraps each result like:
 *   { docid: "shreyansh26", profile: { first_name, experiences, ... }, score: 4, insights: null }
 *
 * The real profile payload lives under the `profile` key, the unique identifier
 * Pearch uses (and the one `/ingest` expects as `selected_ids`) is the top-level
 * `docid`, and the fit score is the top-level `score`.
 *
 * All helpers here accept the raw result item and normalize field access, so
 * callers don't have to care which level a field lives on.
 */

// --- Normalization --------------------------------------------------

// Return the inner profile payload whether the result is wrapped
// ({docid, profile, score}) or flat (just a profile). Falls back to the
// result itself so callers never have to null-check.
const unwrap = (item) => {
  if (!item) return {}
  if (item.profile && typeof item.profile === "object") return item.profile
  return item
}

// --- Identity -------------------------------------------------------

// The `docid` on the wrapper is Pearch's stable source identifier and is what
// the `/sourcing/ingest` endpoint expects as `selected_ids`. We prefer that
// over any nested id so the selection set matches what we'll post server-side.
export const getProfileId = (item) => {
  if (!item) return ""
  const profile = unwrap(item)
  return (
    item.docid ||
    item.source_id ||
    profile.docid ||
    profile.source_id ||
    profile.id ||
    profile.linkedin_slug ||
    ""
  )
}

// --- Name / initials ------------------------------------------------

export const getFullName = (item) => {
  const profile = unwrap(item)
  if (profile.full_name) return profile.full_name
  if (profile.name) return profile.name
  const parts = [profile.first_name, profile.last_name].filter(Boolean)
  if (parts.length) return parts.join(" ")
  return profile.linkedin_slug || ""
}

export const getInitials = (item) => {
  const name = getFullName(item)
  if (!name) return "?"
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase()
}

// --- Role / company -------------------------------------------------

export const getCurrentRole = (item) => {
  const profile = unwrap(item)
  const experiences = profile.experiences || profile.experience
  if (Array.isArray(experiences) && experiences.length > 0) {
    // Prefer the explicitly-marked current experience when present.
    const pickCurrent = (exp) => {
      if (!exp) return null
      if (exp.company_info || exp.company_roles) {
        const currentRole =
          exp.company_roles?.find((r) => r.is_current_experience) ||
          exp.company_roles?.[0]
        return {
          title: currentRole?.title || "",
          company: exp.company_info?.name || currentRole?.company || "",
        }
      }
      return { title: exp.title || "", company: exp.company || "" }
    }

    const firstCurrent = experiences
      .map(pickCurrent)
      .find((r) => r && r.title)
    if (firstCurrent) return firstCurrent

    // Fallback to the first experience's first role.
    return pickCurrent(experiences[0]) || { title: "", company: "" }
  }

  // Last resort — use the headline field if present (some profiles have it).
  return { title: profile.title || profile.headline || "", company: "" }
}

// --- Location -------------------------------------------------------

export const getLocation = (item) => {
  const profile = unwrap(item)
  if (profile.location) return profile.location

  const parts = [profile.city, profile.state, profile.country].filter(Boolean)
  return parts.join(", ")
}

// --- Skills / expertise ---------------------------------------------

// Pearch surfaces a free-text tag list as `expertise`, and some responses also
// include a richer `skills` array. Try structured skills first, then expertise.
export const getSkills = (item, max = 6) => {
  const profile = unwrap(item)
  const source =
    profile.skills ||
    profile.skill_descriptions ||
    profile.expertise ||
    []
  if (!Array.isArray(source)) return []
  return source
    .map((s) => (typeof s === "string" ? s : s.skill || s.name || ""))
    .filter(Boolean)
    .slice(0, max)
}

// --- Experience meta ------------------------------------------------

export const getYearsOfExperience = (item) => {
  const profile = unwrap(item)
  if (typeof profile.total_experience_years === "number") {
    return profile.total_experience_years
  }
  if (typeof profile.years_of_experience === "number") {
    return profile.years_of_experience
  }
  const experiences = profile.experiences || profile.experience
  if (!Array.isArray(experiences) || experiences.length === 0) return null
  return experiences.length
}

// --- Picture --------------------------------------------------------

// Pearch surfaces a LinkedIn-hosted profile photo as `picture_url`. It's often
// there, sometimes it's a generic grey silhouette (`static.licdn.com/aero-v1/`),
// and very occasionally it's missing altogether. Callers should use this in a
// plain <img> and fall back to initials on `onError` — the aero-v1 placeholder
// is technically valid but we filter it out so we show initials instead of the
// same grey silhouette for every missing-photo profile.
const LINKEDIN_PLACEHOLDER_MARKER = "aero-v1/sc/h/9c8pery4"

export const getPictureUrl = (item) => {
  const profile = unwrap(item)
  const url = profile.picture_url || profile.photo_url || profile.avatar_url || ""
  if (!url || typeof url !== "string") return ""
  if (url.includes(LINKEDIN_PLACEHOLDER_MARKER)) return ""
  return url
}

// --- Contact --------------------------------------------------------

export const getLinkedInUrl = (item) => {
  const profile = unwrap(item)
  if (profile.linkedin_url) return profile.linkedin_url
  if (profile.linkedin_slug) return `https://linkedin.com/in/${profile.linkedin_slug}`
  return ""
}

// Pearch splits contacts across several fields:
//   - best_business_email  (single promoted address)
//   - business_emails[]    (all work addresses they could find)
//   - personal_emails[]    (gmail/yahoo/etc.)
// We merge them into a deduped, best-first ordered list.
export const getEmails = (item) => {
  const profile = unwrap(item)
  const seen = new Set()
  const out = []
  const push = (email) => {
    if (!email || typeof email !== "string") return
    const key = email.toLowerCase().trim()
    if (!key || seen.has(key)) return
    seen.add(key)
    out.push(email)
  }

  push(profile.best_business_email)
  if (Array.isArray(profile.business_emails)) profile.business_emails.forEach(push)
  if (Array.isArray(profile.personal_emails)) profile.personal_emails.forEach(push)

  // Legacy fallbacks (some pipelines emit a flat `emails`/`email`).
  const legacy = profile.emails || profile.email
  if (Array.isArray(legacy)) legacy.forEach(push)
  else if (typeof legacy === "string") push(legacy)

  return out
}

// Pearch stores phones as parallel arrays `phone_numbers` + `phone_types`
// (e.g. ["+212…", "+212…"] / ["mobile", "mobile"]). We zip them into
// `{ number, type }` so the dossier can label each entry.
export const getPhones = (item) => {
  const profile = unwrap(item)
  const numbers = Array.isArray(profile.phone_numbers) ? profile.phone_numbers : null

  if (numbers && numbers.length) {
    const types = Array.isArray(profile.phone_types) ? profile.phone_types : []
    return numbers
      .map((number, i) => ({
        number,
        type: types[i] || "",
      }))
      .filter((p) => p.number)
  }

  // Legacy fallback — flat `phones`/`phone`.
  const raw = profile.phones || profile.phone
  if (Array.isArray(raw)) return raw.filter(Boolean).map((n) => ({ number: n, type: "" }))
  if (typeof raw === "string" && raw) return [{ number: raw, type: "" }]
  return []
}

// Social / portfolio URLs surfaced by Pearch.
export const getWebsites = (item) => {
  const profile = unwrap(item)
  const sites = profile.websites
  if (!Array.isArray(sites)) return []
  return sites.filter((u) => typeof u === "string" && u.trim())
}

// --- Education ------------------------------------------------------

// Map Pearch's degree tags ("bachelor", "doctor", …) to the labels
// recruiters expect to read.
const DEGREE_LABELS = {
  bachelor: "Bachelor's",
  master: "Master's",
  doctor: "PhD",
  phd: "PhD",
  associate: "Associate",
  diploma: "Diploma",
  certificate: "Certificate",
}

// Flatten Pearch's `educations` into a uniform { campus, degree, major,
// start_date, end_date } shape for the dossier — same pattern as
// getExperienceEntries for experiences.
export const getEducationEntries = (item, max = 6) => {
  const profile = unwrap(item)
  const educations = profile.educations || profile.education || []
  if (!Array.isArray(educations)) return []

  return educations.slice(0, max).map((edu) => {
    const degreeTags = Array.isArray(edu.degree) ? edu.degree : []
    const degree = degreeTags
      .map((d) => DEGREE_LABELS[String(d).toLowerCase()] || d)
      .filter(Boolean)
      .join(" / ")
    return {
      campus: edu.campus || "",
      degree,
      major: edu.major || "",
      specialization: edu.specialization || "",
      country: edu.country || "",
      start_date: edu.start_date || "",
      end_date: edu.end_date || "",
    }
  })
}

// --- Fit score ------------------------------------------------------

// Pearch's top-level `score` is a 0–5 integer ("how relevant is this
// candidate"). Normalize to a 0–100 percentage so the progress bar and number
// read naturally to recruiters. We also accept a 0–1 float (some inference
// pipelines surface it that way) and a 0–100 pass-through (defensive).
export const getFitScore = (item) => {
  if (!item) return null
  const profile = unwrap(item)
  const raw =
    item.score ??
    profile.fit_score ??
    profile.profile_score ??
    profile.score ??
    profile.relevance_score
  if (typeof raw !== "number" || Number.isNaN(raw)) return null
  if (raw <= 1) return Math.round(raw * 100)
  if (raw <= 5) return Math.round(raw * 20)
  return Math.round(Math.min(raw, 100))
}

// --- Experiences list (for the dossier drawer) ----------------------

// Normalize each experience entry into a flat { title, company, start_date,
// end_date, description } shape so the detail modal doesn't have to branch
// on Pearch's nested company_info/company_roles layout.
export const getExperienceEntries = (item, max = 6) => {
  const profile = unwrap(item)
  const experiences = profile.experiences || profile.experience || []
  if (!Array.isArray(experiences)) return []

  const out = []
  for (const exp of experiences) {
    if (exp.company_info || exp.company_roles) {
      const company = exp.company_info?.name || ""
      const roles = Array.isArray(exp.company_roles) ? exp.company_roles : []
      // Each role under a company is its own timeline entry — a single
      // candidate often holds multiple titles at the same company.
      for (const role of roles) {
        out.push({
          title: role.title || "",
          company: role.company || company,
          start_date: role.start_date || "",
          end_date: role.end_date || (role.is_current_experience ? "current" : ""),
          description: role.experience_summary || role.description || "",
        })
        if (out.length >= max) return out
      }
    } else {
      out.push({
        title: exp.title || "",
        company: exp.company || "",
        start_date: exp.start_date || "",
        end_date: exp.end_date || "",
        description: exp.description || "",
      })
      if (out.length >= max) return out
    }
  }
  return out
}

export const SUGGESTIONS = [
  {
    tagKey: "sourcing.suggestions.engineering.tag",
    textKey: "sourcing.suggestions.engineering.text",
    queryKey: "sourcing.suggestions.engineering.text",
  },
  {
    tagKey: "sourcing.suggestions.sales.tag",
    textKey: "sourcing.suggestions.sales.text",
    queryKey: "sourcing.suggestions.sales.text",
  },
  {
    tagKey: "sourcing.suggestions.design.tag",
    textKey: "sourcing.suggestions.design.text",
    queryKey: "sourcing.suggestions.design.text",
  },
]
